import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import log from '@/lib/logger';

const EMAIL_TEMPLATES_ADMIN_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/email-templates`;

export interface EmailTemplateResponse {
    id: string;
    templateType: string;
    subject: string;
    body: string;
    createdAt: string;
    updatedAt?: string;
}

export interface TemplateTypeInfo {
    type: string;
    placeholders: string[];
    dummyValues: Record<string, string>;
}

export interface SaveEmailTemplateRequest {
    subject: string;
    body: string;
}

export interface SendTestEmailRequest {
    recipientEmail: string;
}

export async function getEmailTemplateTypes(token: string | null): Promise<TemplateTypeInfo[] | void> {
    if (!token) {
        log.error("No token provided for email template types retrieval.");
        return Promise.reject("No token provided");
    }

    const response = await fetch(`${EMAIL_TEMPLATES_ADMIN_URL}/types`, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    });

    return handleResponse<TemplateTypeInfo[]>(response);
}

export async function getEmailTemplate(
    token: string | null,
    templateType: string,
): Promise<EmailTemplateResponse | null | void> {
    if (!token) {
        log.error("No token provided for email template retrieval.");
        return Promise.reject("No token provided");
    }

    const response = await fetch(`${EMAIL_TEMPLATES_ADMIN_URL}/${templateType}`, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    });

    if (response.status === 204) {
        return null;
    }

    return handleResponse<EmailTemplateResponse>(response);
}

export async function saveEmailTemplate(
    token: string | null,
    templateType: string,
    request: SaveEmailTemplateRequest,
): Promise<EmailTemplateResponse | void> {
    if (!token) {
        log.error("No token provided for email template save.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${EMAIL_TEMPLATES_ADMIN_URL}/${templateType}`, {
        method: 'PUT',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });

    return handleResponse<EmailTemplateResponse>(response);
}

export async function sendTestEmail(
    token: string | null,
    templateType: string,
    request: SendTestEmailRequest,
): Promise<void> {
    if (!token) {
        log.error("No token provided for test email send.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${EMAIL_TEMPLATES_ADMIN_URL}/${templateType}/test`, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });

    return handleResponse<void>(response);
}
